'use client';

import { useMemo } from 'react';
import { useURLState, useURLArrayState, useURLRangeState, useURLClearAll } from './useURLState';
import type { FilterState, SortOption } from '@/types/cars';

const resetPagination = { resetKeys: ['page'] };

/**
 * Comprehensive hook for managing all car filter state via URL parameters
 */
export function useCarFilters(bounds?: {
  minYear?: number;
  maxYear?: number;
  minMileage?: number;
  maxMileage?: number;
  minPrice?: number;
  maxPrice?: number;
}) {
  // Default bounds that will be overridden by facets data
  const defaultBounds = {
    minYear: 2000,
    maxYear: 2025,
    minMileage: 0,
    maxMileage: 200000,
    minPrice: 0,
    maxPrice: 100000,
  };
  
  const rangeBounds = bounds ? { ...defaultBounds, ...bounds } : defaultBounds;
  // Single value filters
  const [searchQuery, setSearchQuery] = useURLState('q', '', resetPagination);
  const [sortOption, setSortOption] = useURLState<SortOption>('sort', 'newest', resetPagination);
  const [page, setPage] = useURLState('page', 1);
  const [shopOnline, setShopOnline] = useURLState('shopOnline', true, resetPagination);
  const [selectedCollection, setSelectedCollection] = useURLState('collection', '', resetPagination);
  
  // Array filters (multiple selections)
  const [selectedMakes, setSelectedMakes] = useURLArrayState('make', resetPagination);
  const [selectedModels, setSelectedModels] = useURLArrayState('model', resetPagination);
  const [selectedBodyStyles, setSelectedBodyStyles] = useURLArrayState('bodystyle', resetPagination);
  const [selectedTransmissions, setSelectedTransmissions] = useURLArrayState('transmission', resetPagination);
  const [selectedFuelTypes, setSelectedFuelTypes] = useURLArrayState('fuel', resetPagination);
  const [selectedDriveTrains, setSelectedDriveTrains] = useURLArrayState('train', resetPagination);
  const [selectedCylinders, setSelectedCylinders] = useURLArrayState('cylinder', resetPagination);
  const [selectedEngines, setSelectedEngines] = useURLArrayState('engine', resetPagination);
  const [selectedDoors, setSelectedDoors] = useURLArrayState('doors', resetPagination);
  const [selectedExteriorColors, setSelectedExteriorColors] = useURLArrayState('excolor', resetPagination);
  const [selectedInteriorColors, setSelectedInteriorColors] = useURLArrayState('incolor', resetPagination);
  
  // Range filters
  const [priceRange, setPriceRange] = useURLRangeState('price', [rangeBounds.minPrice, rangeBounds.maxPrice], resetPagination);
  const [yearRange, setYearRange] = useURLRangeState('year', [rangeBounds.minYear, rangeBounds.maxYear], resetPagination);
  const [mileageRange, setMileageRange] = useURLRangeState('mileage', [rangeBounds.minMileage, rangeBounds.maxMileage], resetPagination);
  
  // Clear all filters
  const [clearAllFilters] = useURLClearAll(['q']); // Preserve search query when clearing filters
  
  // Computed values
  const filterState: FilterState = useMemo(() => ({
    ...(selectedCollection && { collection: selectedCollection }),
    ...(searchQuery && { q: searchQuery }),
    sort: sortOption,
    ...(page > 1 && { page }),
    shopOnline,
    ...(selectedMakes.length > 0 && { make: selectedMakes }),
    ...(selectedModels.length > 0 && { model: selectedModels }),
    ...(selectedBodyStyles.length > 0 && { bodystyle: selectedBodyStyles }),
    ...(selectedTransmissions.length > 0 && { transmission: selectedTransmissions }),
    ...(selectedFuelTypes.length > 0 && { fuel: selectedFuelTypes }),
    ...(selectedDriveTrains.length > 0 && { train: selectedDriveTrains }),
    ...(selectedCylinders.length > 0 && { cylinder: selectedCylinders }),
    ...(selectedEngines.length > 0 && { engine: selectedEngines }),
    ...(selectedDoors.length > 0 && { doors: selectedDoors.map(Number) }),
    ...(selectedExteriorColors.length > 0 && { excolor: selectedExteriorColors }),
    ...(selectedInteriorColors.length > 0 && { incolor: selectedInteriorColors }),
    ...((priceRange[0] !== rangeBounds.minPrice || priceRange[1] !== rangeBounds.maxPrice) && { price: priceRange }),
    ...((yearRange[0] !== rangeBounds.minYear || yearRange[1] !== rangeBounds.maxYear) && { year: yearRange }),
    ...((mileageRange[0] !== rangeBounds.minMileage || mileageRange[1] !== rangeBounds.maxMileage) && { mileage: mileageRange }),
  }), [
    selectedCollection, searchQuery, sortOption, page, shopOnline,
    selectedMakes, selectedModels, selectedBodyStyles, selectedTransmissions,
    selectedFuelTypes, selectedDriveTrains, selectedCylinders, selectedEngines,
    selectedDoors, selectedExteriorColors, selectedInteriorColors,
    priceRange, yearRange, mileageRange, rangeBounds
  ]);
  
  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return selectedMakes.length > 0 ||
           Boolean(selectedCollection) ||
           selectedModels.length > 0 ||
           selectedBodyStyles.length > 0 ||
           selectedTransmissions.length > 0 ||
           selectedFuelTypes.length > 0 ||
           selectedDriveTrains.length > 0 ||
           selectedCylinders.length > 0 ||
           selectedEngines.length > 0 ||
           selectedDoors.length > 0 ||
           selectedExteriorColors.length > 0 ||
           selectedInteriorColors.length > 0 ||
           (priceRange[0] !== rangeBounds.minPrice || priceRange[1] !== rangeBounds.maxPrice) ||
           (yearRange[0] !== rangeBounds.minYear || yearRange[1] !== rangeBounds.maxYear) ||
           (mileageRange[0] !== rangeBounds.minMileage || mileageRange[1] !== rangeBounds.maxMileage);
  }, [
    selectedCollection, selectedMakes, selectedModels, selectedBodyStyles, selectedTransmissions,
    selectedFuelTypes, selectedDriveTrains, selectedCylinders, selectedEngines,
    selectedDoors, selectedExteriorColors, selectedInteriorColors,
    priceRange, yearRange, mileageRange, rangeBounds
  ]);
  
  return {
    // Current filter state
    filterState,
    hasActiveFilters,
    
    // Single value filters
    searchQuery,
    setSearchQuery,
    sortOption,
    setSortOption,
    page,
    setPage,
    shopOnline,
    setShopOnline,
    selectedCollection,
    setSelectedCollection,
    
    // Array filters
    selectedMakes,
    setSelectedMakes,
    selectedModels,
    setSelectedModels,
    selectedBodyStyles,
    setSelectedBodyStyles,
    selectedTransmissions,
    setSelectedTransmissions,
    selectedFuelTypes,
    setSelectedFuelTypes,
    selectedDriveTrains,
    setSelectedDriveTrains,
    selectedCylinders,
    setSelectedCylinders,
    selectedEngines,
    setSelectedEngines,
    selectedDoors,
    setSelectedDoors,
    selectedExteriorColors,
    setSelectedExteriorColors,
    selectedInteriorColors,
    setSelectedInteriorColors,
    
    // Range filters
    priceRange,
    setPriceRange,
    yearRange,
    setYearRange,
    mileageRange,
    setMileageRange,
    
    // Actions
    clearAllFilters
  };
}
